import { Component, useListen } from '@atomify/hooks';
import { useStore } from '@atomify/kit';
import { clearFocus, MapState, mapStore, setFocus } from '@bpd-library/components';
import { ExtendedPropertyType, findExtendedObjectById } from '@bpd-library/utilities';
import { getCurrentView, getFilteredData } from '@features/properties/store';
import { virtualView2dPointerEvents } from '@features/virtual-tour/constants';

import { houseSelectorEvents } from '../../house-selector';

const HIGHLIGHTED_MODIFIER = 'is--highlighted';

const useVisualInteraction = (
    element: { current?: Component | HTMLElement | null },
    scrollContainer: { current?: HTMLDivElement | null },
) => {
    const [, subscribeMap] = useStore<MapState>(mapStore);

    // Subscribe to map
    subscribeMap(
        ({ hover }) => {
            const { type } = getCurrentView() || {};
            switch (type) {
                case 'constructionNumber':
                    if (hover.previous) focusCard(hover.previous.id, false);
                    if (hover.current) focusCard(hover.current.id, true);

                    break;
                case 'propertyType':
                    if (hover.previous) handlePropertyTypeViewHover(hover.previous.id, false);
                    if (hover.current) handlePropertyTypeViewHover(hover.current.id, true);

                    break;
            }
        },
        ['hover'],
    );

    // Listen to apartment view events
    useListen(document, virtualView2dPointerEvents.LABEL_HOVER, ({ detail }) =>
        focusCard(detail.id, detail.active),
    );

    // Listen to House selector events
    useListen(document, houseSelectorEvents.POLYGON_HOVER, ({ detail }) => {
        const { type } = getCurrentView() || {};

        if (type === 'constructionNumber' || type === 'apartmentConstructionNumber') {
            focusCard(detail.id, detail.active);
        } else if (type === 'propertyType' || type === 'apartmentPropertyType') {
            handlePropertyTypeViewHover(detail.id, detail.active);
        }
    });

    function handlePropertyTypeViewHover(mapDataItemId: string, active: boolean) {
        const filteredData = getFilteredData();

        if (filteredData) {
            const extendedObject = findExtendedObjectById(filteredData, mapDataItemId);
            switch (extendedObject?.objectType) {
                case 'constructionNumber':
                    if (extendedObject.apartmentBuilding?.id) {
                        focusCard(extendedObject.apartmentBuilding.id, active);
                        return;
                    }
                    if (extendedObject.propertyType) {
                        focusPropertyTypeCard(extendedObject.propertyType, active);
                    }
                    return;
                case 'propertyType':
                    focusPropertyTypeCard(extendedObject, active);
                    return;
                case 'apartmentBuilding':
                case 'projectPhase':
                    focusCard(extendedObject.id, active);
                    return;
            }
        }
    }

    function focusPropertyTypeCard(propertyType: ExtendedPropertyType, active: boolean) {
        const relatedConstructionNumberIds =
            propertyType.constructionNumbers?.map(({ id }) => id) || [];

        if (active) {
            setFocus([...relatedConstructionNumberIds, propertyType.id], { static: true });
        } else {
            clearFocus();
        }

        focusCard(propertyType.id, active);
    }

    function focusCard(cardId: string, active: boolean) {
        const card = element.current?.querySelector<HTMLElement>(`#container-${cardId}`);

        if (card) {
            card.classList[active ? 'add' : 'remove'](HIGHLIGHTED_MODIFIER);

            if (active) {
                const { top } = card.getBoundingClientRect();
                const windowCenter = window.innerHeight * 0.4;
                const currentScroll = scrollContainer.current?.scrollTop || 0;
                const targetScroll = currentScroll + top - windowCenter;

                // Apply to html
                scrollContainer.current?.scroll({
                    top: targetScroll,
                    left: 0,
                    behavior: 'smooth',
                });
            }
        }
    }
};

export { useVisualInteraction };
